import React from 'react';
import { Card, Image, Badge, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';

import sustainabilityHubLogo from '@/assets/img/logos/png/blueBackground.png';

/**
 * MemberCard Component
 * Displays a community member's profile card with avatar, name, and info
 */
const MemberCard = ({
    userId = '',
    username = '',
    profilePicture = '',
    bio = '',
    organization = '',
    userType = '',
    role = 'member',
    status = 'active',
    joinedAt = '',
    membership = null,
    onBan = null,
    onUnban = null,
    isSiteAdmin = false,
    isModeratorOfCommunity = false,
    currentUserId = '',
}) => {
    // TODO: Use userId to link to user profile page when implemented
    void userId;
    const displayName = username ? `@${username}` : 'Unknown User';

    // Format joined date
    const joinedDate = joinedAt
        ? new Date(joinedAt).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
          })
        : null;

    // Role badge variant
    const getRoleBadge = () => {
        if (membership?.user?.role === 'admin') {
            return (
                <Badge bg="warning" className="ms-2 text-dark">
                    Admin
                </Badge>
            );
        }
        if (role === 'moderator') {
            return (
                <Badge bg="success" className="ms-2">
                    Moderator
                </Badge>
            );
        }
        return null;
    };

    // Role badge variant
    const getStatusBadge = () => {
        if (status === 'banned') {
            return (
                <div className="d-flex align-items-center justify-content-center mb-2">
                    <Badge bg="danger" className="ms-2">
                        Banned
                    </Badge>
                </div>
            );
        }
        if (status === 'pending') {
            return (
                <div className="d-flex align-items-center justify-content-center mb-2">
                    <Badge bg="warning" className="ms-2">
                        Pending
                    </Badge>
                </div>
            );
        }
        return null;
    };

    // Format user type for display
    const formatUserType = (type) => {
        if (!type) return null;
        return type.charAt(0).toUpperCase() + type.slice(1);
    };

    return (
        <Card className="member-card h-100 border-0 shadow-sm">
            <Card.Body className="d-flex flex-column align-items-center text-center p-4">
                {/* Profile Picture */}
                <Image
                    src={profilePicture || sustainabilityHubLogo}
                    onError={(e) => {
                        e.target.src = sustainabilityHubLogo;
                    }}
                    alt={`${displayName}'s profile`}
                    roundedCircle
                    className="member-avatar mb-3"
                />

                {/* Name and Role */}
                <div className="d-flex align-items-center justify-content-center mb-2">
                    <h6 className="mb-0 fw-bold text-primary">{displayName}</h6>
                    {getRoleBadge()}
                </div>

                {/* Status */}
                {getStatusBadge()}

                {/* User Type */}
                {userType && (
                    <span className="badge bg-light text-info mb-2">
                        {formatUserType(userType)}
                    </span>
                )}

                {/* Organization */}
                {organization && (
                    <p className="small text-muted mb-2">
                        <i className="bi bi-building me-1" aria-hidden="true" />
                        {organization}
                    </p>
                )}

                {/* Bio (truncated) */}
                {bio && (
                    <p className="small text-info mb-2 member-bio">
                        {bio.length > 80 ? `${bio.substring(0, 80)}...` : bio}
                    </p>
                )}

                {/* Joined Date */}
                {joinedDate && (
                    <p className="small text-muted mb-0 mt-auto">
                        Joined {joinedDate}
                    </p>
                )}
                {/* Ban button — visible to site admins and community moderators only */}
                {(isSiteAdmin || isModeratorOfCommunity) &&
                    onBan &&
                    userId !== currentUserId &&
                    status !== 'banned' &&
                    membership?.user?.role !== 'admin' &&
                    !(role === 'moderator' && !isSiteAdmin) && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            className="mt-3 w-100"
                            onClick={() => onBan(membership._id)}
                            aria-label={`Ban ${username || 'this member'}`}
                        >
                            <i
                                className="bi bi-slash-circle me-1"
                                aria-hidden="true"
                            />
                            Ban Member
                        </Button>
                    )}

                {/* Unban button — visible to site admins and community moderators only */}
                {(isSiteAdmin || isModeratorOfCommunity) &&
                    onUnban &&
                    userId !== currentUserId &&
                    status === 'banned' &&
                    !(role === 'moderator' && !isSiteAdmin) && (
                        <Button
                            variant="outline-warning"
                            size="sm"
                            className="mt-3 w-100 text-dark"
                            onClick={() => onUnban(membership)}
                            aria-label={`Unban ${username || 'this member'}`}
                        >
                            <i
                                className="bi bi-slash-circle me-1"
                                aria-hidden="true"
                            />
                            Unban Member
                        </Button>
                    )}
            </Card.Body>
        </Card>
    );
};

MemberCard.propTypes = {
    userId: PropTypes.string,
    username: PropTypes.string,
    profilePicture: PropTypes.string,
    bio: PropTypes.string,
    organization: PropTypes.string,
    userType: PropTypes.string,
    role: PropTypes.oneOf(['member', 'moderator', 'admin']),
    status: PropTypes.oneOf(['active', 'banned', 'pending']),
    joinedAt: PropTypes.string,
    membership: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        role: PropTypes.oneOf(['member', 'moderator', 'admin']),
        status: PropTypes.oneOf(['active', 'banned', 'pending']),
        joinedAt: PropTypes.string,
        // Ban audit fields — populated only when status === 'banned'
        bannedBy: PropTypes.string, // resolved to a username (or null)
        bannedReason: PropTypes.string,
        bannedAt: PropTypes.string,
        user: PropTypes.shape({
            _id: PropTypes.string,
            username: PropTypes.string,
            profilePicture: PropTypes.string,
            bio: PropTypes.string,
            organization: PropTypes.string,
            userType: PropTypes.string,
            role: PropTypes.string,
        }),
    }).isRequired,
    onBan: PropTypes.func,
    onUnban: PropTypes.func,
    isSiteAdmin: PropTypes.bool,
    isModeratorOfCommunity: PropTypes.bool,
    currentUserId: PropTypes.string,
};

export default MemberCard;
